import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import type { ChatIntentMode } from '../../../../common/utils/quick-find/chat-intent-mode';

/**
 * Keeps the keyboard-highlighted row in sync with the currently selected chat-intent
 * mode. This is a trimmed copy of the Jira `useIntentRouterKeyboardPreselection`: it
 * keeps the same shared KeyboardHighlightProvider behaviour and re-apply guards, but
 * drops the matching-work-item row (the chat router only ever renders two rows).
 *
 * Callers inject `trackingIdByMode`, which maps each mode to the DOM tracking id of
 * its row (must equal the `SearchResult` `${resultType}:${id}` value).
 *
 * Guard logic runs synchronously in useLayoutEffect (before paint) to prevent race
 * conditions with arrow key navigation. The actual highlight application is deferred
 * to a requestAnimationFrame for smoother visual transitions.
 */
export const useChatIntentKeyboardPreselection = ({
	primaryMode,
	query,
	trackingIdByMode,
	inputRef,
}: {
	primaryMode: ChatIntentMode;
	query: string;
	trackingIdByMode: Record<ChatIntentMode, string>;
	inputRef?: RefObject<HTMLInputElement>;
}): void => {
	const { highlightComponentAt, unhighlightAll, keyboardNavComponents, getSelectedIndex } =
		useKeyboardHighlightProvider() ?? {};

	const setActiveDescendant = useCallback(
		(trackingId: string | null) => {
			if (!fg('search_qf_announce_programmatic_highlight')) {
				return;
			}
			const input = inputRef?.current;
			if (!input) {
				return;
			}
			if (trackingId) {
				input.setAttribute('aria-activedescendant', trackingId);
			} else {
				input.removeAttribute('aria-activedescendant');
			}
		},
		[inputRef],
	);

	const lastAppliedModeRef = useRef<ChatIntentMode | null>(null);
	const prevQueryRef = useRef(query);
	const highlightRafIdRef = useRef<number | null>(null);

	useLayoutEffect(() => {
		const currentIdx = getSelectedIndex?.() ?? -1;

		const desiredTrackingId = trackingIdByMode[primaryMode];

		const desiredIdx =
			keyboardNavComponents?.findIndex((c) => c.navigationTrackingId === desiredTrackingId) ?? -1;

		if (desiredIdx < 0) {
			return;
		}

		if (lastAppliedModeRef.current === primaryMode) {
			const queryChanged = prevQueryRef.current !== query;
			prevQueryRef.current = query;

			if (currentIdx === desiredIdx) {
				return;
			}

			if (currentIdx >= 0 && !queryChanged) {
				return;
			}
		}

		lastAppliedModeRef.current = primaryMode;

		if (highlightRafIdRef.current !== null) {
			cancelAnimationFrame(highlightRafIdRef.current);
		}

		highlightRafIdRef.current = requestAnimationFrame(() => {
			highlightRafIdRef.current = null;
			unhighlightAll?.();
			highlightComponentAt?.(desiredIdx);
			keyboardNavComponents?.[desiredIdx]?.onHighlightedCallback?.(desiredIdx);
			setActiveDescendant(desiredTrackingId);
		});

		return () => {
			if (highlightRafIdRef.current !== null) {
				cancelAnimationFrame(highlightRafIdRef.current);
				highlightRafIdRef.current = null;
			}
		};
	}, [
		keyboardNavComponents,
		trackingIdByMode,
		primaryMode,
		query,
		getSelectedIndex,
		highlightComponentAt,
		unhighlightAll,
		setActiveDescendant,
	]);
};
